import { Component, OnInit, inject } from '@angular/core';
import { ModalController, AlertController, IonToggle, IonicModule } from '@ionic/angular';
import { Store, select } from '@ngrx/store';
import { Observable, forkJoin, take } from 'rxjs';
import { ChangelogPage } from '../changelog/changelog.page';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TicketService } from '../personal/ticket/ticket.service';
import { ClearItems, FetchFeedList, ResetNews } from 'src/app/ngrx/news/news.actions';
import { AccessibilityDisclaimerPage } from '../accessibility-disclaimer/accessibility-disclaimer.page';
import { LicensesComponent } from '../licenses/licenses.component';
import { Device, DeviceInfo } from '@capacitor/device';
import { Share } from '@capacitor/share';
import { App, AppInfo } from '@capacitor/app';
import { ResetTiles } from 'src/app/ngrx/tile/tile.actions';
import { ResetPushNotification } from 'src/app/ngrx/push-notification/push-notification.actions';
import { CampusIdReset } from 'src/app/ngrx/campus-id/campus-id.actions';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Capacitor } from '@capacitor/core';
import { PushService } from 'src/app/services/push.service';
import { PWAService } from 'src/app/services/pwa.service';
import { HomescreenPage } from '../homescreen/homescreen.page';
import { selectActiveFeedSubscriptions } from 'src/app/ngrx/news/news.reducer';
import { AsyncPipe } from '@angular/common';
import { AppState } from 'src/app/app.module';
import { IPushChannel, IWeatherStyle, IWeatherWindUnit } from 'src/app/ngrx/settings/settings.models';
import { settingsFeature } from 'src/app/ngrx/settings/settings.feature';
import { SettingsActions } from 'src/app/ngrx/settings/settings.actions';
import { AuthService, SignOutType } from 'src/app/oidc/auth.service';

@Component({
    selector: 'app-settings',
    templateUrl: './settings.page.html',
    styleUrls: ['./settings.page.scss'],
    imports: [IonicModule, AsyncPipe, TranslatePipe]
})
export class SettingsPage implements OnInit {
  private store = inject<Store<AppState>>(Store);
  private ticketService = inject(TicketService);
  private alertController = inject(AlertController);
  private http = inject(HttpClient);
  private pushService = inject(PushService);
  public translate = inject(TranslateService);
  public modalController = inject(ModalController);
  public authService = inject(AuthService);

  public debug = false;

  public canteenRemember$: Observable<boolean>;
  public departureRemember$: Observable<boolean>;

  public pushEnabled$: Observable<boolean>;
  public pushChannels$: Observable<{ [K in IPushChannel]: boolean }>;
  
  public weather$: Observable<boolean>;
  public weatherStyle$: Observable<IWeatherStyle>;
  public weatherWindUnit$: Observable<IWeatherWindUnit>;

  public deviceInfo: DeviceInfo | null = null;
  public appInfo: AppInfo | null = null;

  public languages: { lang: string; translationKey: string }[] = [];
  public weatherStyles: { style: string; translationKey: string }[] = [];

  constructor() {
    this.canteenRemember$ = this.store.pipe(select(settingsFeature.selectCanteenRemember));
    this.departureRemember$ = this.store.pipe(select(settingsFeature.selectDepartureRemember));

    this.pushEnabled$ = this.store.pipe(select(settingsFeature.selectPushEnabled));
    this.pushChannels$ = this.store.pipe(select(settingsFeature.selectPushChannels));

    this.weather$ = this.store.pipe(select(settingsFeature.selectWeatherEnabled));
    this.weatherStyle$ = this.store.pipe(select(settingsFeature.selectWeatherStyle));
    this.weatherWindUnit$ = this.store.pipe(select(settingsFeature.selectWeatherWindUnit));
    this.weatherStyles = Object.entries(IWeatherStyle).map(([key, value]): { style: string; translationKey: string } => ({ style: value, translationKey: `settings.weather.${key.toLocaleLowerCase()}` }));
  }

  async ngOnInit() {
    this.debug = !environment.production;

    this.deviceInfo = await Device.getInfo();
    this.appInfo = Capacitor.isNativePlatform() ? await App.getInfo() : await PWAService.getInfo();

    this.languages = this.translate.getLangs().map((value: string) => ({
      lang: value,
      translationKey: `settings.lang.${value}`,
    }));
  }

  public share(): void {
    Share.share({
      title: 'TU Dortmund App',
      text: `${this.translate.instant('settings.share.title')}

iOS: https://apps.apple.com/de/app/tu-dortmund/id987953936
Android: https://play.google.com/store/apps/details?id=de.tudortmund.app`,
      dialogTitle: 'TU Dortmund App',
    });
  }

  public clearCache() {
    forkJoin({
      ticketCache: this.ticketService.removeAllTickets(),
    }).subscribe(() => {
      this.alertController
        .create({
          header: this.translate.instant('settings.information.clearCache.alert.header'),
          message: this.translate.instant('settings.information.clearCache.alert.message'),
          buttons: ['Ok'],
        })
        .then((alert: HTMLIonAlertElement) => {
          alert.present();
        });
    });
  }

  public resetApp() {
    this.alertController
      .create({
        header: this.translate.instant('settings.information.reset.alert.header'),
        message: this.translate.instant('settings.information.reset.alert.message'),
        buttons: [
          {
            text: this.translate.instant('core.yes'),
            handler: () => {
              this.store.dispatch(SettingsActions.resetSettings());
              this.store.dispatch(ResetNews());
              this.store.dispatch(ResetPushNotification());
              this.store.dispatch(CampusIdReset());
              this.store.dispatch(FetchFeedList());
              this.ticketService.removeAllTickets().subscribe();
            },
          },
          {
            text: this.translate.instant('core.no'),
            role: 'cancel',
          },
        ],
      })
      .then((alert: HTMLIonAlertElement) => {
        alert.present();
      });
  }

  public signout(): void {
    this.authService.logout(async () => {
      this.store.dispatch(CampusIdReset());
      this.store.dispatch(ResetTiles());
      this.store.dispatch(ResetPushNotification());
      this.ticketService.removeAllTickets().subscribe();

      const appInfo = Capacitor.isNativePlatform() ? App.getInfo() : PWAService.getInfo();

      // Collect statistics about the logout behavior
      const body = new URLSearchParams();
      body.set('type', SignOutType.USER_INITIATED);
      body.set('version', (await appInfo).version);
      body.set('build', (await appInfo).build);
      body.set('os', (await Device.getInfo()).operatingSystem);
      body.set('os_version', (await Device.getInfo()).osVersion);
      body.set('webViewVersion', (await Device.getInfo()).webViewVersion);
      const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
      this.http.post(`${environment.apiUrl}/logout-statistics`, body, { headers }).subscribe();

      const alert = await this.alertController.create({
        header: this.translate.instant('auth.signOut.userInitiated.title'),
        message: this.translate.instant('auth.signOut.userInitiated.message'),
        buttons: [this.translate.instant('core.alert.ok')],
      });
      alert.present();
    });
  }

  public signin() {
    this.authService.login();
  }

  public toggleCanteenRemember(event: IonToggle): void {
    this.store.dispatch(SettingsActions.toggleCanteenRemember({ enabled: event.checked }));
  }

  public toggleDepartureRemember(event: IonToggle): void {
    this.store.dispatch(SettingsActions.toggleDepartureRemember({ enabled: event.checked }));
  }

  public togglePush(event: IonToggle): void {
    this.store.dispatch(SettingsActions.togglePush({ enabled: event.checked }));
  }

  public sendTestNotification(): void {
    this.pushService.sendTestNotification().subscribe();
  }

  public togglePushChannel(event: IonToggle): void {
    const channel: IPushChannel = event.name as IPushChannel;
    this.store.dispatch(SettingsActions.togglePushChannel({ channel, enabled: event.checked }));
  }

  public toggleWeather(event: IonToggle): void {
    this.store.dispatch(SettingsActions.toggleWeather({ enabled: event.checked }));
  }

  public selectWeatherStyle(event: any) {
    const style = event.detail.value;
    this.store.dispatch(SettingsActions.changeWeatherStyle({ style }));
  }

  public selectWeatherWindUnit(event: any) {
    const windUnit = event.detail.value;
    this.store.dispatch(SettingsActions.changeWeatherWindUnit({ windUnit }));
  }

  public selectLanguage(event: any) {
    const language = event.detail.value;
    this.store.dispatch(SettingsActions.changeLanguage({ language }));

    this.store.pipe(select(selectActiveFeedSubscriptions), take(1)).subscribe((feedIds) => {
      this.store.dispatch(ClearItems({ feedIds }))
    });
  }

  public openChangelogs(): void {
    this.modalController
      .create({
        component: ChangelogPage,
        presentingElement: document.querySelector('ion-modal') as HTMLElement | undefined
      })
      .then((modal: HTMLIonModalElement) => modal.present());
  }

  public openAccessibility(): void {
    this.modalController
      .create({
        component: AccessibilityDisclaimerPage,
        presentingElement: document.querySelector('ion-modal') as HTMLElement | undefined
      })
      .then((modal: HTMLIonModalElement) => modal.present());
  }

  public openLicenses(): void {
    this.modalController
      .create({
        component: LicensesComponent,
        presentingElement: document.querySelector('ion-modal') as HTMLElement | undefined
      })
      .then((modal: HTMLIonModalElement) => modal.present());
  }

  public async openHomescreenSettings() {
    const modal = await this.modalController.create({
      component: HomescreenPage,
      canDismiss: (_, role) => {
        return Promise.resolve(role === 'button');
      },
      presentingElement: document.querySelector('ion-modal') as HTMLElement | undefined
    });
    modal.present();
  }
}
